import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../utils/Api';
import { api_url_v1 } from '../utils/Config';

const initialState = {
  items: [],
  selectedCategory: null,
  loading: false,
  error: null
};

// Fetch all job categories
export const fetchJobCategories = createAsyncThunk(
  'jobCategories/fetchJobCategories',
  async (_, { getState, rejectWithValue }) => {
    try {
      const { auth: { token } } = getState();
      const response = await api.get(`${api_url_v1}/job-categories`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        }
      });
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch job categories');
    }
  }
);

// Create new job category
export const createJobCategory = createAsyncThunk(
  'jobCategories/createJobCategory',
  async (categoryData, { getState, rejectWithValue }) => {
    try {
      const { auth: { token } } = getState();
      const response = await api.post(`${api_url_v1}/job-categories`, categoryData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        }
      });
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to create job category');
    }
  }
);

// Fetch single job category
export const fetchJobCategoryDetails = createAsyncThunk(
  'jobCategories/fetchJobCategoryDetails',
  async (id, { getState, rejectWithValue }) => {
    try {
      const { auth: { token } } = getState();
      const response = await api.get(`${api_url_v1}/job-categories/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        }
      });
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch job category details');
    }
  }
);

// Update job category
export const updateJobCategory = createAsyncThunk(
  'jobCategories/updateJobCategory',
  async ({ id, data }, { getState, rejectWithValue }) => {
    try {
      const { auth: { token } } = getState();
      const response = await api.put(`${api_url_v1}/job-categories/${id}`, data, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        }
      });
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to update job category');
    }
  }
);

// Delete job category
export const deleteJobCategory = createAsyncThunk(
  'jobCategories/deleteJobCategory',
  async (id, { getState, rejectWithValue }) => {
    try {
      const { auth: { token } } = getState();
      await api.delete(`${api_url_v1}/job-categories/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        }
      });
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to delete job category');
    }
  }
);

const jobCategoriesSlice = createSlice({
  name: 'jobCategories',
  initialState,
  reducers: {
    setSelectedCategory: (state, action) => {
      state.selectedCategory = action.payload;
    },
    clearSelectedCategory: (state) => {
      state.selectedCategory = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch job categories cases
      .addCase(fetchJobCategories.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchJobCategories.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload;
      })
      .addCase(fetchJobCategories.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Create job category cases
      .addCase(createJobCategory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createJobCategory.fulfilled, (state, action) => {
        state.loading = false;
        state.items.push(action.payload);
      })
      .addCase(createJobCategory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch job category details cases
      .addCase(fetchJobCategoryDetails.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchJobCategoryDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedCategory = action.payload;
      })
      .addCase(fetchJobCategoryDetails.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Update job category cases
      .addCase(updateJobCategory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateJobCategory.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.items.findIndex(category => category.id === action.payload.id);
        if (index !== -1) {
          state.items[index] = action.payload;
        }
        if (state.selectedCategory?.id === action.payload.id) {
          state.selectedCategory = action.payload;
        }
      })
      .addCase(updateJobCategory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Delete job category cases
      .addCase(deleteJobCategory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteJobCategory.fulfilled, (state, action) => {
        state.loading = false;
        state.items = state.items.filter(category => category.id !== action.payload);
        if (state.selectedCategory?.id === action.payload) {
          state.selectedCategory = null;
        }
      })
      .addCase(deleteJobCategory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  }
});

export const { setSelectedCategory, clearSelectedCategory } = jobCategoriesSlice.actions;
export default jobCategoriesSlice.reducer;